// @ts-nocheck
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Info, CheckCircle, Settings, HardHat } from 'lucide-react';

interface ServiceUnderConstructionProps {
    serviceKey: string;
    logo?: string;
    icon?: React.ReactNode;
    headerImage?: string;
    primaryColor?: string;
    secondaryColor?: string;
    features?: string[];
}

const ServiceUnderConstruction: React.FC<ServiceUnderConstructionProps> = ({
    serviceKey,
    logo,
    icon,
    headerImage = '/assets/images/fonds.jpg', // Default background if available
    primaryColor = '#06036e',
    secondaryColor = '#0A9D0E',
    features
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Use features from props if provided, otherwise from i18n
    const displayFeatures = features || [
        t('services:underConstruction.feature1'),
        t('services:underConstruction.feature2'),
        t('services:underConstruction.feature3')
    ];

    const serviceName = t(`services:serviceData.${serviceKey}.name`, { defaultValue: serviceKey.charAt(0).toUpperCase() + serviceKey.slice(1) });
    const category = t(`services:serviceData.${serviceKey}.category`);
    const description = t(`services:serviceData.${serviceKey}.description`);
    const tagline = t(`services:serviceData.${serviceKey}.tagline`);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-poppins">
            {/* Header */}
            <div className="relative h-48 sm:h-56 overflow-hidden rounded-b-[30px] z-0">
                {headerImage && (
                    <img
                        src={headerImage}
                        alt="Header Background"
                        className="absolute inset-0 w-full h-full object-cover blur-[2px]"
                    />
                )}
                {/* Gradient Overlay */}
                <div
                    className="absolute inset-0 flex flex-col justify-end items-center pb-6 px-4"
                    style={{
                        background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${secondaryColor}dd 100%)`
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center text-white"
                    >
                        <p className="text-xs sm:text-sm font-medium opacity-90 uppercase tracking-wider mb-1">
                            {category}
                        </p>
                        <h1 className="text-2xl sm:text-3xl font-bold drop-shadow-md">
                            {serviceName}
                        </h1>
                    </motion.div>
                </div>

                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="absolute top-6 left-6 w-10 h-10 bg-white/30 hover:bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center transition-all z-10 text-white"
                >
                    <ArrowLeft size={24} />
                </button>
            </div>

            {/* Content Container */}
            <div className="flex-1 px-4 pb-24 -mt-8 z-10 max-w-lg mx-auto w-full">

                {/* Logo Section */}
                <motion.div
                    initial={{ scale: 0.8, y: 0 }}
                    animate={{ scale: 1, y: [0, -10, 0] }}
                    transition={{
                        scale: { duration: 0.5, type: 'spring' },
                        y: { repeat: Infinity, duration: 2, ease: "easeInOut" }
                    }}
                    className="flex justify-center mb-6"
                >
                    <div className="w-28 h-28 sm:w-32 sm:h-32 bg-white rounded-full p-1 shadow-xl border-4 border-white/80 flex items-center justify-center">
                        {icon ? (
                            <div className="w-20 h-20 flex items-center justify-center">
                                {icon}
                            </div>
                        ) : logo ? (
                            <img src={logo} alt={serviceName} className="w-full h-full object-contain rounded-full" />
                        ) : null}
                    </div>
                </motion.div>

                {/* Badge */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="flex items-center justify-center gap-2 bg-orange-50 text-orange-600 px-4 py-2 rounded-full w-fit mx-auto mb-6 border border-orange-100 shadow-sm"
                >
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
                    >
                        <HardHat size={18} />
                    </motion.div>
                    <span className="font-semibold text-sm">{t('services:underConstruction.badge')}</span>
                </motion.div>

                {/* Tagline */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-center text-lg font-semibold text-slate-800 mb-8 px-2"
                >
                    {tagline}
                </motion.p>

                {/* Description Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white/60 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-sm mb-6"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <span style={{ color: primaryColor }}><Info size={24} /></span>
                        <h3 className="font-semibold text-slate-800">{t('services:underConstruction.about')}</h3>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">
                        {description}
                    </p>
                </motion.div>

                {/* Status Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white/60 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-sm mb-6"
                >
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                            >
                                <Settings size={32} className="text-orange-500" />
                            </motion.div>
                        </div>
                    </div>

                    <h3 className="text-center font-bold text-lg text-slate-800 mb-2">
                        {t('services:underConstruction.title')}
                    </h3>
                    <p className="text-center text-slate-500 text-sm mb-6">
                        {t('services:underConstruction.message')}
                    </p>

                    <div className="space-y-3">
                        {displayFeatures.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                                <CheckCircle size={18} className="text-green-600 shrink-0" />
                                <span className="text-slate-600 text-sm font-medium">{feature}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Timeline Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-white/60 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-sm mb-8"
                >
                    <h3 className="font-bold text-slate-800 mb-2">🚀 {t('services:underConstruction.comingSoon')}</h3>
                    <p className="text-slate-600 text-sm">
                        {t('services:underConstruction.stayTuned')}
                    </p>
                </motion.div>

                {/* Home Button */}
                <button
                    onClick={() => navigate('/home')}
                    className="w-full py-4 rounded-xl flex items-center justify-center gap-2 text-white font-semibold shadow-lg hover:brightness-110 transition-all active:scale-[0.98]"
                    style={{ backgroundColor: primaryColor }}
                >
                    <Home size={20} />
                    <span>{t('services:underConstruction.backHome')}</span>
                </button>

            </div>
        </div>
    );
};

export default ServiceUnderConstruction;
